import invariant from "tiny-invariant";
import { batch } from "@tanstack/store";
import { createControlledPromise } from "../utils.js";
function hydrateMatch(deyhydratedMatch) {
  return {
    id: deyhydratedMatch.i,
    __beforeLoadContext: deyhydratedMatch.b,
    loaderData: deyhydratedMatch.l,
    status: deyhydratedMatch.s,
    ssr: deyhydratedMatch.ssr,
    updatedAt: deyhydratedMatch.u,
    error: deyhydratedMatch.e
  };
}
async function hydrate(router) {
  var _a, _b, _c;
  invariant(
    (_a = window.$_TSR) == null ? void 0 : _a.router,
    "Expected to find a dehydrated data on window.$_TSR.router, but we did not. Please file an issue!"
  );
  const { manifest, dehydratedData, lastMatchId } = window.$_TSR.router;
  router.ssr = {
    manifest
  };
  const matches = router.matchRoutes(router.state.location);
  const routeChunkPromise = Promise.all(
    matches.map((match) => {
      const route = router.looseRoutesById[match.routeId];
      return router.loadRouteChunk(route);
    })
  );
  function setMatchForcePending(match) {
    const route = router.looseRoutesById[match.routeId];
    const pendingMinMs = route.options.pendingMinMs ?? router.options.defaultPendingMinMs;
    if (pendingMinMs) {
      const minPendingPromise = createControlledPromise();
      match.minPendingPromise = minPendingPromise;
      match._forcePending = true;
      setTimeout(() => {
        minPendingPromise.resolve();
        router.updateMatch(match.id, (prev) => ({
          ...prev,
          minPendingPromise: void 0,
          _forcePending: void 0
        }));
      }, pendingMinMs);
    }
  }
  let firstNonSsrMatchIndex = void 0;
  matches.forEach((match) => {
    const dehydratedMatch = window.$_TSR.router.matches.find(
      (d) => d.i === match.id
    );
    if (!dehydratedMatch) {
      Object.assign(match, { dehydrated: false, ssr: false });
      return;
    }
    Object.assign(match, hydrateMatch(dehydratedMatch));
    if (match.ssr === false) {
      match._dehydrated = false;
    } else {
      match._dehydrated = true;
    }
    if (match.ssr === "data-only" || match.ssr === false) {
      if (firstNonSsrMatchIndex === void 0) {
        firstNonSsrMatchIndex = match.index;
        setMatchForcePending(match);
      }
    }
  });
  router.__store.setState((s) => {
    return {
      ...s,
      matches
    };
  });
  await ((_c = (_b = router.options).hydrate) == null ? void 0 : _c.call(_b, dehydratedData));
  await Promise.all(
    router.state.matches.map(async (match) => {
      var _a2, _b2, _c2, _d, _e, _f;
      const route = router.looseRoutesById[match.routeId];
      const parentMatch = router.state.matches[match.index - 1];
      const parentContext = (parentMatch == null ? void 0 : parentMatch.context) ?? router.options.context ?? {};
      const contextFnContext = {
        deps: match.loaderDeps,
        params: match.params,
        context: parentContext,
        location: router.state.location,
        navigate: (opts) => router.navigate({ ...opts, _fromLocation: router.state.location }),
        buildLocation: router.buildLocation,
        cause: match.cause,
        abortController: match.abortController,
        preload: false,
        matches
      };
      match.__routeContext = ((_b2 = (_a2 = route.options).context) == null ? void 0 : _b2.call(_a2, contextFnContext)) ?? {};
      match.context = {
        ...parentContext,
        ...match.__routeContext,
        ...match.__beforeLoadContext
      };
      const assetContext = {
        matches: router.state.matches,
        match,
        params: match.params,
        loaderData: match.loaderData
      };
      const headFnContent = await ((_d = (_c2 = route.options).head) == null ? void 0 : _d.call(_c2, assetContext));
      const scripts = await ((_f = (_e = route.options).scripts) == null ? void 0 : _f.call(_e, assetContext));
      match.meta = headFnContent == null ? void 0 : headFnContent.meta;
      match.links = headFnContent == null ? void 0 : headFnContent.links;
      match.headScripts = headFnContent == null ? void 0 : headFnContent.scripts;
      match.styles = headFnContent == null ? void 0 : headFnContent.styles;
      match.scripts = scripts;
    })
  );
  const isSpaMode = matches[matches.length - 1].id !== lastMatchId;
  const hasSsrFalseMatches = matches.some((m) => m.ssr === false);
  if (!hasSsrFalseMatches && !isSpaMode) {
    matches.forEach((match) => {
      match._dehydrated = void 0;
    });
    return routeChunkPromise;
  }
  const loadPromise = Promise.resolve().then(() => router.load()).catch((err) => {
    console.error("Error during router hydration:", err);
  });
  if (isSpaMode) {
    const match = matches[1];
    invariant(
      match,
      "Expected to find a match below the root match in SPA mode."
    );
    setMatchForcePending(match);
    match._displayPending = true;
    match.displayPendingPromise = loadPromise;
    loadPromise.then(() => {
      batch(() => {
        if (router.__store.state.status === "pending") {
          router.__store.setState((s) => ({
            ...s,
            status: "idle",
            resolvedLocation: s.location
          }));
        }
        router.updateMatch(match.id, (prev) => {
          return {
            ...prev,
            _displayPending: void 0,
            displayPendingPromise: void 0
          };
        });
      });
    });
  }
  return routeChunkPromise;
}
export {
  hydrate
};
//# sourceMappingURL=ssr-client.js.map
